import { CdkColumnDef, CdkTable } from '@angular/cdk/table';
import {
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    TemplateRef,
    TrackByFunction,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { ApiDefaults, LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import { Subject } from 'rxjs';
import { delay, map, scan, shareReplay } from 'rxjs/operators';
import { Icons } from '../../../constants/icons.constants';
import { Messages } from '../../../constants/messages.constants';
import { Table } from '../../../constants/table.constants';
import { TableAppearance } from '../../../table/models/table-appearance';
import { LifecycleAware } from '../../../utilities/rxjs.helpers';

@LifecycleAware()
@Component({
    selector: 'lsq-data-table',
    template: `
        <div
            *ngIf="
                (dataSource.loading$ | async) &&
                hasSpinner &&
                !isHeaderOnly &&
                !isFooterOnly &&
                (!(dataSource.rows$ | async) || (dataSource.noData$ | async)) &&
                !(appearance === tableAppearance.ExpandableRow)
            "
            class="table-circle-loader"
        >
            <lsq-circle-loader></lsq-circle-loader>
        </div>
        <div
            [ngStyle]="{
                display:
                    (columnDefs$ | async) &&
                    ((dataSource.rows$ | async) ||
                        (dataSource.noData$ | async) ||
                        isHeaderOnly ||
                        isFooterOnly)
                        ? 'block'
                        : 'none'
            }"
        >
            <div
                class="table-wrapper"
                [ngClass]="{
                    'expandable-row': appearance === tableAppearance.ExpandableRow
                }"
            >
                <lsq-data-table-sticky-header
                    *ngIf="
                        hasStickyHeader &&
                        (columnDefs$ | async) &&
                        !isFooterOnly &&
                        ((dataSource.rows$ | async) || isHeaderOnly) &&
                        (!(dataSource.noData$ | async) || isHeaderOnly)
                    "
                    [columnNames]="displayedColumnKeys$ | async"
                    [getTableHeaderRef]="getTableHeaderRef"
                    [dataSource]="dataSource"
                    [columnDefs]="columnDefs$ | async"
                    (stickyChange)="headerIsStuck = $event"
                >
                </lsq-data-table-sticky-header>

                <!-- TODO: The classes table-base, main-table, and table-loading are needed for waitForMainTableToFinishLoading() -->
                <table
                    cdk-table
                    class="table table-base main-table"
                    [hidden]="dataSource.noData$ | async"
                    [ngClass]="{
                        loading: dataSource.loading$ | async,
                        'table-loading': dataSource.loading$ | async,
                        'fixed-layout': layoutFixed,
                        'cards-table': appearance === tableAppearance.Cards,
                        'appearance-tall': appearance === tableAppearance.Tall,
                        'footer-only-table': isFooterOnly
                    }"
                    [dataSource]="dataSource"
                    [trackBy]="trackBy"
                >
                    <ng-container
                        *ngIf="
                            hasHeader &&
                            !(appearance === tableAppearance.ExpandableRow) &&
                            !isFooterOnly
                        "
                    >
                        <tr
                            #tableHeaderRef
                            cdk-header-row
                            *cdkHeaderRowDef="displayedColumnKeys$ | async"
                        ></tr>
                    </ng-container>

                    <lsq-column
                        [colSpan]="(displayedColumnKeys$ | async)?.length || 1"
                        [column]="{
                            key: 'loadingColumn',
                            width: '100%',
                            sortable: false
                        }"
                    >
                        <ng-template custom-cell let-row="row" let-index="index">
                            <div class="loading-row-content">
                                <lsq-circle-loader></lsq-circle-loader>
                            </div>
                        </ng-template>
                    </lsq-column>

                    <ng-container *ngIf="!isHeaderOnly && !isFooterOnly">
                        <tr
                            cdk-row
                            *cdkRowDef="let row; columns: displayedColumnKeys$ | async"
                            [class]="getRowClassName ? getRowClassName(row) : ''"
                            [ngClass]="{
                                'card-row': appearance === tableAppearance.Cards,
                                'tall-row': appearance === tableAppearance.Tall,
                                'disabled-row':
                                    (!!disableRow ? disableRow(row) : false) || disableTable,
                                'checked-row': isRowSelected ? isRowSelected(row) : false
                            }"
                        ></tr>

                        <tr
                            cdk-row
                            [class]="getRowClassName ? getRowClassName(row) : ''"
                            *cdkRowDef="
                                let row;
                                columns: loadingColumnKey$ | async;
                                when: isRowLoading
                            "
                        ></tr>
                    </ng-container>

                    <ng-container *ngIf="hasFooter || isFooterOnly">
                        <tr
                            cdk-footer-row
                            class="totals"
                            *cdkFooterRowDef="displayedColumnKeys$ | async"
                        ></tr>
                    </ng-container>
                </table>

                <ng-container
                    *ngIf="
                        !(dataSource.loading$ | async) &&
                        (dataSource.noData$ | async) &&
                        !isHeaderOnly &&
                        !isFooterOnly
                    "
                >
                    <ng-container *ngIf="!noDataContent">
                        <div class="no-result-wrapper">
                            <lsq-icon [type]="icons.NoDataReport"></lsq-icon>
                            <p class="text-text-gray">
                                {{ noDataMessage ? noDataMessage : defaultNoDataMessage }}
                            </p>
                        </div>
                    </ng-container>

                    <ng-container *ngIf="noDataContent">
                        <ng-container *ngTemplateOutlet="noDataContent"></ng-container>
                    </ng-container>
                </ng-container>
            </div>

            <lsq-data-table-pagination
                *ngIf="isPaginated && !(dataSource.noData$ | async)"
                [skip]="dataSource.skip ? dataSource.skip : 0"
                [take]="dataSource.take ? dataSource.take : defaultTake"
                [totalCount]="dataSource.totalCount$ | async"
                [takeOptions]="takeOptions"
                [isLoading]="dataSource.loading$ | async"
                (skipChange)="handleSkipChange($event)"
                (takeChange)="handleTakeChange($event)"
                [disableTakeSelect]="disableTakeSelect"
            >
            </lsq-data-table-pagination>
        </div>
    `,
    styleUrls: ['./lsq-data-table.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class LsqDataTableComponent implements OnDestroy, OnChanges {
    @ViewChild(CdkTable) public table: CdkTable<any>;
    @ViewChild('tableHeaderRef', { read: ElementRef }) public tableHeaderRef: ElementRef;
    @ContentChild('noDataContent', { read: TemplateRef }) public noDataContent;
    @Input() public dataSource: LsqListSource<any>;
    @Input() public isPaginated = false;
    @Input() public hasHeader = true;
    @Input() public hasFooter = false;
    @Input() public hasStickyHeader = false;
    @Input() public hasSpinner = true;
    @Input() public isHeaderOnly = false;
    @Input() public isFooterOnly = false;
    @Input() public takeOptions = Table.DefaultTakeOptions;
    @Input() public disableTakeSelect = false;
    @Input() public noDataMessage?: string;
    @Input() public appearance = TableAppearance.Default;
    @Input() public disableTable = false;
    @Input() public disableRow?: (row: any) => boolean;
    @Input() public getRowClassName?: (row: any) => string;
    @Input() public layoutFixed = false;
    @Input() public columnVisibilities?: any;
    @Output() public skipChanged = new EventEmitter<number>();
    @Output() public takeChanged = new EventEmitter<number>();

    public headerIsStuck = false;

    // Constants.
    public icons = Icons;
    public defaultTake = ApiDefaults.TakeDefault;
    public tableAppearance = TableAppearance;
    public defaultNoDataMessage = Messages.NoResultsFound;

    private _columnDefAdditions = new Subject<CdkColumnDef>();
    public columnDefs$ = this._columnDefAdditions.pipe(
        scan((columnDefs, columnDef) => [...columnDefs, columnDef], [] as CdkColumnDef[]),
        delay(0), // Avoid ExpressionChanged error, as `_columnDefAdditions` is `next`ed
        // by this component's ContentChildren (via `addColumn`).
        shareReplay(1)
    );
    private _columnKeyVisibilityMapUpdates = new Subject<{ [key: string]: boolean }>();
    private _columnKeyVisibilityMap$ = this._columnKeyVisibilityMapUpdates.pipe(
        scan(
            (visibilityMap, update) => ({ ...visibilityMap, ...update }),
            {} as { [key: string]: boolean }
        )
    );
    public displayedColumnKeys$ = this._columnKeyVisibilityMap$.pipe(
        map((visibilityMap) =>
            Object.keys(visibilityMap).filter((columnKey) => visibilityMap[columnKey])
        ),
        map((visibleColumns) => visibleColumns.filter((column) => column !== 'loadingColumn')),
        delay(0), // Avoid ExpressionChanged error, as `_columnDefAdditions` is `next`ed
        // by this component's ContentChildren (via `addColumn`).
        shareReplay(1)
    );

    public loadingColumnKey$ = this._columnKeyVisibilityMap$.pipe(
        map((visibilityMap) =>
            Object.keys(visibilityMap).filter((columnKey) => visibilityMap[columnKey])
        ),
        map((visibleColumns) => visibleColumns.filter((column) => column === 'loadingColumn')),
        delay(0), // Avoid ExpressionChanged error, as `_columnDefAdditions` is `next`ed
        // by this component's ContentChildren (via `addColumn`).
        shareReplay(1)
    );
    @Input() public isRowLoading?: (index, row) => boolean = (index, row) => row.isRowLoading;
    @Input() public isRowSelected = () => false;
    @Input() public trackBy: TrackByFunction<any> = (index) => index;
    public getTableHeaderRef = (): ElementRef => this.tableHeaderRef;

    public ngOnDestroy(): void {}

    public ngOnChanges(): void {
        this._updateColumnVisibilities();
    }

    public addColumn(columnDef: CdkColumnDef): void {
        this.table.addColumnDef(columnDef);
        this._columnDefAdditions.next(columnDef);
        this._initializeColumnVisibility(columnDef.name);
    }

    private _initializeColumnVisibility(columnKey: string): void {
        if (this.columnVisibilities && Object.keys(this.columnVisibilities).includes(columnKey)) {
            this._columnKeyVisibilityMapUpdates.next({
                [columnKey]: this.columnVisibilities[columnKey]
            });
        } else {
            this._columnKeyVisibilityMapUpdates.next({
                [columnKey]: true
            });
        }
    }

    private _updateColumnVisibilities(): void {
        if (this.columnVisibilities) {
            Object.keys(this.columnVisibilities).forEach((columnKey) => {
                this._columnKeyVisibilityMapUpdates.next({
                    [columnKey]: this.columnVisibilities[columnKey]
                });
            });
        }
    }

    public handleSkipChange(newSkip: number): void {
        this.dataSource.handleSkipChange(newSkip);
        this.skipChanged.emit(newSkip);
    }

    public handleTakeChange(newTake: number): void {
        this.dataSource.handleTakeChange(newTake);
        this.takeChanged.emit(newTake);
    }
}
